import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiUrl, assetUrl } from '../config/api';
import { isLoggedIn, storeAuthSession } from '../utils/session';
import { InstallAppButton } from '../components/InstallApp';
import PasswordInput from '../components/PasswordInput';
import './Login.css';

const STRINGS = {
  en: {
    title: 'Sangram Sena',
    subtitle: 'Community volunteers for Ahmedabad — sign in to continue.',
    email: 'Email',
    password: 'Password',
    signIn: 'Sign in',
    placeholderEmail: 'you@example.com',
    placeholderPassword: 'Enter password',
    loginError: 'Sign in failed',
  },
  gu: {
    title: 'સંગ્રામ સેના',
    subtitle: 'અમદાવાદ માટે સમુદાય સ્વયંસેવકો — આગળ વધવા સાઇન ઇન કરો.',
    email: 'ઇમેઇલ',
    password: 'પાસવર્ડ',
    signIn: 'સાઇન ઇન',
    placeholderEmail: 'you@example.com',
    placeholderPassword: 'પાસવર્ડ દાખલ કરો',
    loginError: 'સાઇન ઇન નિષ્ફળ',
  },
  hi: {
    title: 'संग्राम सेना',
    subtitle: 'अहमदाबाद के लिए समुदाय स्वयंसेवक — आगे बढ़ने के लिए साइन इन करें।',
    email: 'ईमेल',
    password: 'पासवर्ड',
    signIn: 'साइन इन',
    placeholderEmail: 'you@example.com',
    placeholderPassword: 'पासवर्ड दर्ज करें',
    loginError: 'साइन इन विफल',
  },
};

const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'gu', label: 'ગુજરાતી' },
  { code: 'hi', label: 'हिंदी' },
];

function redirectByRole(navigate, role, status) {
  if (status === 'Active' && role === 'Admin') {
    navigate('/admin', { replace: true });
    return;
  }
  if (status === 'Active' && role === 'Area Head') {
    // Land on profile so Area Heads can manage their account; SOS stays in the menu.
    navigate('/profile', { replace: true });
    return;
  }
  /* Volunteers, and anyone not yet approved, land on their profile / status page */
  navigate('/profile', { replace: true });
}

function Login() {
  const navigate = useNavigate();
  const [lang, setLang] = useState('en');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    const se = sessionStorage.getItem('sangramUserEmail');
    if (se) setEmail(se);
  }, []);

  useEffect(() => {
    if (!isLoggedIn()) return;
    const r = sessionStorage.getItem('sangramUserRole');
    const st = sessionStorage.getItem('sangramUserStatus');
    redirectByRole(navigate, r, st);
  }, [navigate]);

  const t = STRINGS[lang] || STRINGS.en;

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !password) {
      setFormError('Email and password are required.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(apiUrl('/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFormError(data.error || t.loginError);
        return;
      }
      const user = data.user;
      if (!data.token) {
        setFormError('Login succeeded but no token was returned.');
        return;
      }
      storeAuthSession(user, trimmedEmail, data.token);
      redirectByRole(navigate, user.role, user.status);
    } catch {
      setFormError('Network error. Is the server running?');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-shell">
        <div className="login-card">
          <div className="login-brand">
            <div className="login-logo-wrap">
              <img
                className="login-logo"
                src={assetUrl('/uploads/logos/logo.png')}
                alt="Sangram Sena — tiger emblem"
              />
            </div>
            <h1 className="login-title">{t.title}</h1>
            <p className="login-sub">{t.subtitle}</p>
          </div>

          <form className="login-form" onSubmit={onSubmit} autoComplete="on">
            {formError ? (
              <p className="login-form-error" role="alert">
                {formError}
              </p>
            ) : null}

            <label className="login-label">
              {t.email}
              <input
                className="login-input"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.placeholderEmail}
                required
                autoComplete="email"
              />
            </label>

            <label className="login-label">
              {t.password}
              <PasswordInput
                inputClassName="login-input"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t.placeholderPassword}
                autoComplete="current-password"
                required
                minLength={1}
              />
            </label>

            <button className="login-submit" type="submit" disabled={submitting}>
              {submitting ? 'Signing in…' : t.signIn}
            </button>
          </form>

          <p className="login-apply-wrap">
            <Link className="login-apply-link" to="/apply">
              Apply to join Sangram Sena
            </Link>
          </p>

          <div className="login-install-wrap">
            <InstallAppButton className="login-install-btn" />
          </div>

        </div>
      </div>

      <div className="lang-bar" role="group" aria-label="Language">
        {LANGS.map((l) => (
          <button
            key={l.code}
            type="button"
            className={`lang-btn${lang === l.code ? ' active' : ''}`}
            onClick={() => setLang(l.code)}
          >
            {l.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default Login;
